// src/pages/Profile.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import pb from '../lib/pb';
import PostCard, { getMediaItems, injectStyles, useMfTheme } from '../shared/PostCard';
import Navbar from '../components/Navbar';
import { useAuth } from '../contexts/AuthContext';
import { useBrand } from '../brand/context/BrandContext';
import { FriendAvatar, FileImg, fileUrlCandidates, userName, normalizeLinks } from '../components/FriendTile';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import PhotoCameraRoundedIcon from '@mui/icons-material/PhotoCameraRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import ChatBubbleRoundedIcon from '@mui/icons-material/ChatBubbleRounded';
import PersonAddAlt1RoundedIcon from '@mui/icons-material/PersonAddAlt1Rounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';
import WorkOutlineRoundedIcon from '@mui/icons-material/WorkOutlineRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import GridViewRoundedIcon from '@mui/icons-material/GridViewRounded';
import ImageRoundedIcon from '@mui/icons-material/ImageRounded';
import VideocamRoundedIcon from '@mui/icons-material/VideocamRounded';
import NotesRoundedIcon from '@mui/icons-material/NotesRounded';
import RepeatRoundedIcon from '@mui/icons-material/RepeatRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';

/* ------------------------------------------------------------------ */
/* Styles (theme tokens come from the same --mf-* set as the Feed)      */
/* ------------------------------------------------------------------ */
const PROFILE_CSS = `
.pf{min-height:100vh;color:var(--fr-text);--pf-pad:clamp(12px,3vw,32px);
  background:radial-gradient(1000px 380px at 50% -140px,var(--mf-glow,transparent),transparent 70%),var(--mf-bg,#0f131c)}

/* ---------- top bar ---------- */
.pf-topbar{position:sticky;top:0;z-index:50;background:var(--mf-bg-top,var(--fr-bg));border-bottom:1px solid var(--fr-border)}
.pf-topbar-in{max-width:1040px;margin:0 auto;padding:10px var(--pf-pad);display:flex;align-items:center;gap:12px}
.pf-topbar h1{flex:1;min-width:0;margin:0;font-size:17px;font-weight:800;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.pf-back{width:38px;height:38px;border-radius:12px;border:1px solid var(--fr-border-strong);background:var(--fr-bg);color:var(--fr-text);
  display:grid;place-items:center;cursor:pointer;flex:none;transition:background-color .15s}
.pf-back:hover{background:var(--fr-bg2)}
.pf-back:focus-visible{outline:2px solid var(--fr-accent);outline-offset:2px}

.pf-wrap{max-width:1040px;margin:0 auto;padding:16px var(--pf-pad) 90px}

/* ---------- cover ---------- */
.pf-cover{position:relative;height:clamp(170px,28vw,300px);border-radius:22px;overflow:hidden;background:var(--fr-bg3);border:1px solid var(--fr-border)}
.pf-cover img,.pf-cover video{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;display:block}
.pf-cover::after{content:'';position:absolute;inset:0;background:linear-gradient(to top,rgba(0,0,0,.28),transparent 45%);pointer-events:none}
.pf-cover-edit{position:absolute;right:14px;bottom:14px;z-index:2}
.pf-glass{display:inline-flex;align-items:center;gap:6px;height:36px;padding:0 14px;border-radius:999px;border:1px solid rgba(255,255,255,.28);
  background:rgba(0,0,0,.5);color:#fff;font:inherit;font-size:13px;font-weight:700;cursor:pointer;backdrop-filter:blur(8px);transition:background-color .15s}
.pf-glass:hover{background:rgba(0,0,0,.68)}
.pf-glass svg{font-size:18px}
.pf-glass:disabled{opacity:.6;cursor:wait}

/* ---------- identity card ---------- */
.pf-card{position:relative;margin:-44px 14px 0;padding:0 clamp(16px,3vw,28px) 22px;background:var(--fr-bg);border:1px solid var(--fr-border);border-radius:22px;box-shadow:var(--fr-shadow)}
.pf-top{display:flex;align-items:flex-end;justify-content:space-between;gap:14px;flex-wrap:wrap;margin-top:-56px}
.pf-ava{position:relative;border-radius:36px;padding:5px;background:var(--fr-bg);box-shadow:0 8px 24px rgba(0,0,0,.28)}
.pf-ava-edit{position:absolute;right:-2px;bottom:-2px;width:36px;height:36px;border-radius:50%;border:3px solid var(--fr-bg);background:var(--fr-grad);color:#fff;
  display:grid;place-items:center;cursor:pointer}
.pf-ava-edit svg{font-size:18px}
.pf-ava-edit:disabled{opacity:.6;cursor:wait}
.pf-actions{display:flex;gap:8px;flex-wrap:wrap;padding-bottom:6px}
.pf-actions .fr-btn{height:38px;padding:0 16px;font-size:13.5px;border-radius:12px}

.pf-id{margin-top:14px;display:flex;flex-direction:column;gap:8px;min-width:0}
.pf-name{margin:0;font-size:clamp(22px,4vw,30px);font-weight:800;line-height:1.15;letter-spacing:-.02em;overflow-wrap:anywhere}
.pf-handle{font-size:14px;color:var(--fr-muted)}
.pf-chips{display:flex;flex-wrap:wrap;gap:8px 14px;font-size:13.5px;color:var(--fr-muted)}
.pf-chips span{display:inline-flex;align-items:center;gap:5px}
.pf-chips svg{font-size:17px;color:var(--fr-faint)}
.pf-bio{margin:2px 0 0;max-width:68ch;font-size:15px;line-height:1.6;color:var(--fr-text);white-space:pre-wrap;overflow-wrap:anywhere}
.pf-bio--empty{color:var(--fr-faint);font-style:italic}
.pf-links{display:flex;flex-wrap:wrap;gap:8px}
.pf-link{display:inline-flex;align-items:center;gap:5px;max-width:100%;padding:6px 12px;border-radius:999px;background:var(--fr-accent-soft);color:var(--fr-accent);
  font-size:13px;font-weight:700;text-decoration:none;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.pf-link:hover{filter:brightness(1.1)}
.pf-link svg{font-size:16px}
.pf-copy{display:inline-flex;align-items:center;gap:6px;align-self:flex-start;padding:4px 10px;border-radius:999px;border:1px dashed var(--fr-border-strong);background:transparent;
  color:var(--fr-muted);font:inherit;font-size:12.5px;cursor:pointer}
.pf-copy:hover{color:var(--fr-text);border-color:var(--fr-accent)}
.pf-copy svg{font-size:15px}

.pf-stats{display:flex;flex-wrap:wrap;gap:10px;margin-top:18px}
.pf-stat{flex:1 1 110px;max-width:200px;padding:12px 14px;border-radius:14px;background:var(--fr-bg2);border:1px solid var(--fr-border);text-align:left;color:inherit;font:inherit}
button.pf-stat{cursor:pointer;transition:border-color .15s,background-color .15s}
button.pf-stat:hover{border-color:var(--fr-accent);background:var(--fr-bg3)}
.pf-stat b{display:block;font-size:20px;line-height:1.1}
.pf-stat span{font-size:12px;color:var(--fr-faint);font-weight:600}

/* ---------- tabs ---------- */
.pf-tabs{display:flex;gap:6px;margin:22px 0 14px;padding:4px;background:var(--fr-bg);border:1px solid var(--fr-border);border-radius:999px;overflow-x:auto;scrollbar-width:none;
  max-width:max-content}
.pf-tabs::-webkit-scrollbar{display:none}
.pf-tab{flex:none;display:inline-flex;align-items:center;gap:6px;border:0;background:transparent;color:var(--fr-muted);font:inherit;font-size:13.5px;font-weight:700;
  padding:9px 14px;border-radius:999px;cursor:pointer;white-space:nowrap;transition:background-color .15s,color .15s}
.pf-tab:hover{color:var(--fr-text)}
.pf-tab:focus-visible{outline:2px solid var(--fr-accent);outline-offset:1px}
.pf-tab.is-active{background:var(--fr-tab);color:var(--fr-text);box-shadow:0 1px 3px rgba(0,0,0,.22)}
.pf-tab svg{font-size:18px}
.pf-tab-n{font-size:11px;font-weight:800;padding:1px 7px;border-radius:999px;background:var(--fr-bg3);color:var(--fr-muted)}

/* ---------- feed ---------- */
.pf-feed{max-width:720px;margin:0 auto;display:flex;flex-direction:column;gap:16px;min-width:0}
.pf-reshare-by{margin:0 0 8px;font-size:13px;font-weight:700;color:var(--fr-muted);display:flex;align-items:center;gap:6px}
.pf-reshare-by svg{font-size:17px}
.pf-empty{text-align:center;padding:44px 22px;background:var(--fr-bg);border:1px dashed var(--fr-border-strong);border-radius:18px;color:var(--fr-muted)}
.pf-empty-ico{width:54px;height:54px;border-radius:16px;margin:0 auto 12px;display:grid;place-items:center;background:var(--fr-accent-soft);color:var(--fr-accent);font-size:24px}
.pf-empty h3{margin:0 0 4px;color:var(--fr-text);font-size:17px}
.pf-empty p{margin:0;font-size:14px}

.pf-sk{border-radius:14px;background:linear-gradient(90deg,var(--fr-bg2) 25%,var(--fr-bg3) 37%,var(--fr-bg2) 63%);background-size:400% 100%;animation:fr-shimmer 1.4s ease infinite}

/* ---------- modal ---------- */
.pf-modal{position:fixed;inset:0;z-index:1200;display:flex;align-items:center;justify-content:center;padding:14px;background:var(--fr-overlay)}
.pf-dialog{width:100%;max-width:520px;max-height:calc(100vh - 28px);display:flex;flex-direction:column;background:var(--fr-bg);color:var(--fr-text);
  border:1px solid var(--fr-border-strong);border-radius:20px;box-shadow:0 24px 60px rgba(0,0,0,.45);animation:fr-pop .2s ease-out;overflow:hidden}
.pf-dialog-head{display:flex;align-items:center;justify-content:space-between;gap:10px;padding:16px 18px;border-bottom:1px solid var(--fr-border)}
.pf-dialog-head h2{margin:0;font-size:17px;font-weight:800}
.pf-dialog-body{padding:16px 18px;overflow-y:auto;display:flex;flex-direction:column;gap:14px}
.pf-dialog-foot{display:flex;justify-content:flex-end;gap:8px;padding:14px 18px;border-top:1px solid var(--fr-border);background:var(--fr-bg2)}
.pf-x{width:34px;height:34px;border-radius:10px;border:0;background:var(--fr-bg2);color:var(--fr-muted);display:grid;place-items:center;cursor:pointer}
.pf-x:hover{background:var(--fr-bg3);color:var(--fr-text)}

.pf-field{display:flex;flex-direction:column;gap:6px}
.pf-field>label,.pf-label{font-size:12.5px;font-weight:800;letter-spacing:.02em;color:var(--fr-muted)}
.pf-field input[type=text],.pf-field textarea{width:100%;padding:10px 12px;border-radius:12px;border:1px solid var(--fr-border-strong);background:var(--fr-bg2);color:var(--fr-text);
  font:inherit;font-size:14.5px;outline:none}
.pf-field textarea{min-height:96px;resize:vertical;line-height:1.5}
.pf-field input:focus,.pf-field textarea:focus{border-color:var(--fr-accent);box-shadow:0 0 0 3px var(--fr-accent-soft)}
.pf-linkrow{display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1.5fr) auto;gap:8px}
@media (max-width:520px){.pf-linkrow{grid-template-columns:1fr auto}.pf-linkrow input:nth-child(2){grid-column:1/2}}
.pf-switch{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:12px 14px;border-radius:14px;background:var(--fr-bg2);border:1px solid var(--fr-border);cursor:pointer}
.pf-switch strong{display:block;font-size:14px}
.pf-switch small{color:var(--fr-faint);font-size:12.5px}
.pf-switch input{appearance:none;flex:none;width:44px;height:26px;border-radius:999px;background:var(--fr-bg3);position:relative;cursor:pointer;transition:background-color .2s;border:1px solid var(--fr-border-strong)}
.pf-switch input::after{content:'';position:absolute;top:2px;left:2px;width:20px;height:20px;border-radius:50%;background:#fff;transition:transform .2s}
.pf-switch input:checked{background:var(--fr-accent);border-color:transparent}
.pf-switch input:checked::after{transform:translateX(18px)}
.pf-switch input:focus-visible{outline:2px solid var(--fr-accent);outline-offset:2px}

.pf-people{list-style:none;margin:0;padding:0;display:flex;flex-direction:column;gap:8px}
.pf-person{display:flex;align-items:center;gap:12px;padding:10px;border-radius:14px;background:var(--fr-bg2);border:1px solid var(--fr-border);color:inherit;text-decoration:none;cursor:pointer;width:100%;font:inherit;text-align:left}
.pf-person:hover{border-color:var(--fr-border-strong)}
.pf-person b{display:block;font-size:14.5px}
.pf-person small{color:var(--fr-faint)}

.pf-toast{position:fixed;left:50%;bottom:calc(24px + env(safe-area-inset-bottom));transform:translateX(-50%);z-index:2000;max-width:calc(100vw - 28px);padding:11px 16px;border-radius:12px;
  background:#111827;color:#fff;font-size:13.5px;font-weight:600;border:1px solid rgba(255,255,255,.12);box-shadow:0 10px 30px rgba(0,0,0,.4);animation:fr-toast-in .22s ease-out}

@media (max-width:767px){
  .pf-card{margin:-40px 0 0}
  .pf-actions{width:100%}
  .pf-actions .fr-btn{flex:1 1 auto}
  .pf-tabs{max-width:100%}
  .pf-feed{margin:0 calc(var(--pf-pad) * -1)}
  .pf-feed>*,.pf-feed .pf-reshare{min-width:0}
  .pf-feed .mf-post{border-radius:0;border-left-width:0;border-right-width:0;box-shadow:none}
  .pf-reshare-by{padding:0 var(--pf-pad)}
  .pf-empty{margin:0 var(--pf-pad)}
}
@media (prefers-reduced-motion:reduce){.pf *{animation:none!important;transition:none!important}}
`;
injectStyles('mf-profile-css', PROFILE_CSS);

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */
const MAX_LINKS = 5;
const MAX_UPLOAD_MB = 80;

function postTypes(post) {
  const set = new Set(getMediaItems(post).map((i) => i.type));
  const mt = String(post.mediaType || '').toLowerCase();
  if (mt === 'audio') set.add('music');
  else if (mt) set.add(mt);
  return set;
}

const TAB_DEFS = [
  { id: 'all', label: 'All', Icon: GridViewRoundedIcon },
  { id: 'images', label: 'Images', Icon: ImageRoundedIcon },
  { id: 'videos', label: 'Videos', Icon: VideocamRoundedIcon },
  { id: 'text', label: 'Text', Icon: NotesRoundedIcon },
  { id: 'reshares', label: 'Reshares', Icon: RepeatRoundedIcon },
];

const matchesTab = (post, tab) => {
  if (tab === 'all') return true;
  const types = postTypes(post);
  if (tab === 'images') return types.has('image');
  if (tab === 'videos') return types.has('video');
  if (tab === 'text') return types.has('text') || types.size === 0;
  return true;
};

const fmt = (n) => Intl.NumberFormat(undefined, { notation: 'compact' }).format(Number(n) || 0);

function Empty({ icon, title, text }) {
  return (
    <div className="pf-empty">
      <div className="pf-empty-ico" aria-hidden="true">{icon}</div>
      <h3>{title}</h3>
      {text && <p>{text}</p>}
    </div>
  );
}

function Modal({ title, onClose, children, footer }) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return (
    <div className="pf-modal" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="pf-dialog" role="dialog" aria-modal="true" aria-label={title}>
        <div className="pf-dialog-head">
          <h2>{title}</h2>
          <button type="button" className="pf-x" aria-label="Close" onClick={onClose}><CloseRoundedIcon /></button>
        </div>
        <div className="pf-dialog-body">{children}</div>
        {footer && <div className="pf-dialog-foot">{footer}</div>}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Profile                                                             */
/* ------------------------------------------------------------------ */
export default function Profile() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const { hasBrand } = useBrand();
  const [theme, toggleTheme] = useMfTheme();

  const [profile, setProfile] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [posts, setPosts] = useState([]);
  const [reshares, setReshares] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(true);
  const [tab, setTab] = useState('all');
  const [isFollowing, setIsFollowing] = useState(false);
  const [followBusy, setFollowBusy] = useState(false);
  const [showFollowers, setShowFollowers] = useState(false);
  const [followersList, setFollowersList] = useState([]);
  const [loadingFollowers, setLoadingFollowers] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState('');
  const [toast, setToast] = useState('');
  const [form, setForm] = useState({ name: '', profession: '', city: '', bio: '', links: [], showPublicProfile: true });

  const avatarInput = useRef(null);
  const coverInput = useRef(null);
  const toastTimer = useRef(null);

  const isMe = user?.id === id;

  const showToast = (text) => {
    setToast(text);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(''), 3200);
  };
  useEffect(() => () => clearTimeout(toastTimer.current), []);

  /* ---------- load ---------- */
  useEffect(() => {
    let alive = true;
    setProfile(null);
    setNotFound(false);
    setPosts([]);
    setReshares([]);
    setTab('all');
    setEditing(false);
    setShowFollowers(false);

    (async () => {
      try {
        const u = await pb.collection('users').getOne(id, { $autoCancel: false });
        if (!alive) return;
        setProfile(u);
      } catch (err) {
        console.error('Failed to load profile', err);
        if (alive) setNotFound(true);
        return;
      }

      setLoadingPosts(true);
      try {
        const [p, r] = await Promise.all([
          pb.collection('posts').getFullList({
            filter: `author="${id}"`,
            expand: 'author',
            sort: '-created',
            $autoCancel: false,
          }),
          pb.collection('reshares').getFullList({
            filter: `author="${id}"`,
            expand: 'post,post.author',
            sort: '-created',
            $autoCancel: false,
          }).catch(() => []),
        ]);
        if (!alive) return;
        setPosts(p);
        setReshares(r);
      } catch (err) {
        console.error('Failed to load profile feed', err);
      } finally {
        if (alive) setLoadingPosts(false);
      }
    })();

    return () => { alive = false; };
  }, [id]);

  // am I following this person?
  useEffect(() => {
    if (!user?.id || isMe) { setIsFollowing(false); return undefined; }
    let alive = true;
    pb.collection('follows')
      .getFirstListItem(`follower="${user.id}" && following="${id}"`, { $autoCancel: false })
      .then(() => alive && setIsFollowing(true))
      .catch(() => alive && setIsFollowing(false));
    return () => { alive = false; };
  }, [user?.id, id, isMe]);

  // realtime updates to the profile
  useEffect(() => {
    let cancelled = false;
    let unsub = null;
    try {
      Promise.resolve(pb.collection('users').subscribe(id, (e) => {
        if (e.action === 'update') setProfile((prev) => ({ ...(prev || {}), ...e.record }));
      })).then((fn) => {
        if (cancelled) { try { fn?.(); } catch { /* ignore */ } } else unsub = fn;
      }).catch(() => {});
    } catch { /* realtime is optional */ }
    return () => {
      cancelled = true;
      try { unsub?.(); } catch { /* ignore */ }
    };
  }, [id]);

  /* ---------- actions ---------- */
  const uploadFile = async (field, file) => {
    if (!file || !profile) return;
    const isVideo = file.type.startsWith('video');
    const okType = field === 'cover' ? (file.type.startsWith('image') || isVideo) : file.type.startsWith('image');
    if (!okType) { showToast(field === 'cover' ? 'Please choose an image or a video.' : 'Please choose an image.'); return; }
    if (file.size > MAX_UPLOAD_MB * 1024 * 1024) { showToast(`That file is too large (max ${MAX_UPLOAD_MB} MB).`); return; }
    setUploading(field);
    try {
      const fd = new FormData();
      fd.append(field, file);
      if (field === 'cover') fd.append('coverType', isVideo ? 'video' : 'image');
      const updated = await pb.collection('users').update(profile.id, fd);
      setProfile(updated);
      showToast(field === 'cover' ? 'Cover updated' : 'Photo updated');
    } catch (err) {
      console.error(`${field} upload error`, err);
      showToast('Upload failed. Please try again.');
    } finally {
      setUploading('');
    }
  };

  const openEditor = () => {
    const links = Array.isArray(profile.linkLabels)
      ? profile.linkLabels.map((l) => (typeof l === 'string' ? { label: '', url: l } : { label: l?.label || '', url: l?.url || '' }))
      : normalizeLinks(profile.linkLabels).map((l) => ({ label: l.label, url: l.url }));
    setForm({
      name: profile.name || '',
      profession: profile.profession || '',
      city: profile.city || '',
      bio: profile.bio || '',
      links,
      showPublicProfile: profile.showPublicProfile ?? true,
    });
    setEditing(true);
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      const links = form.links
        .map((l) => ({ label: l.label.trim(), url: l.url.trim() }))
        .filter((l) => l.url)
        .slice(0, MAX_LINKS);
      const updated = await pb.collection('users').update(profile.id, {
        name: form.name.trim(),
        profession: form.profession.trim(),
        city: form.city.trim(),
        bio: form.bio,
        linkLabels: links,
        showPublicProfile: form.showPublicProfile,
      });
      setProfile(updated);
      setEditing(false);
      showToast('Profile saved');
    } catch (err) {
      console.error('Save profile failed', err);
      showToast('Could not save your profile.');
    } finally {
      setSaving(false);
    }
  };

  const openFollowers = async () => {
    setShowFollowers(true);
    setLoadingFollowers(true);
    try {
      const list = await pb.collection('follows').getFullList({
        filter: `following="${id}"`,
        expand: 'follower',
        sort: '-created',
        $autoCancel: false,
      });
      setFollowersList(list);
    } catch (err) {
      console.error(err);
      setFollowersList([]);
    } finally {
      setLoadingFollowers(false);
    }
  };

  const toggleFollow = async () => {
    if (!user) { nav('/login'); return; }
    setFollowBusy(true);
    try {
      if (isFollowing) {
        const existing = await pb.collection('follows')
          .getFirstListItem(`follower="${user.id}" && following="${id}"`, { $autoCancel: false })
          .catch(() => null);
        if (existing) await pb.collection('follows').delete(existing.id);
        setIsFollowing(false);
        setProfile((p) => ({ ...p, followersCount: Math.max(0, (p.followersCount || 0) - 1) }));
        showToast('Unfollowed');
      } else {
        await pb.collection('follows').create({ follower: user.id, following: id });
        setIsFollowing(true);
        setProfile((p) => ({ ...p, followersCount: (p.followersCount || 0) + 1 }));
        showToast(`You're following ${userName(profile)}`);
      }
    } catch (err) {
      console.error(err);
      showToast('Something went wrong. Please try again.');
    } finally {
      setFollowBusy(false);
    }
  };

  const doLogout = () => {
    pb.authStore.clear();
    logout?.();
    nav('/login');
  };

  const goToBrand = () => {
    if (!user) { nav('/login'); return; }
    nav(hasBrand ? '/brand' : '/brand/setup');
  };

  const copyPeerId = async () => {
    try {
      await navigator.clipboard.writeText(profile.peerId);
      showToast('ID copied');
    } catch {
      showToast('Could not copy the ID.');
    }
  };

  const handlePostDeleted = (postId) => setPosts((prev) => prev.filter((p) => p.id !== postId));

  /* ---------- derived ---------- */
  const links = useMemo(() => normalizeLinks(profile?.linkLabels), [profile?.linkLabels]);
  const coverUrls = useMemo(
    () => fileUrlCandidates(profile, profile?.cover),
    [profile?.id, profile?.cover, profile?.collectionId],
  );
  const counts = useMemo(() => ({
    all: posts.length,
    images: posts.filter((p) => matchesTab(p, 'images')).length,
    videos: posts.filter((p) => matchesTab(p, 'videos')).length,
    text: posts.filter((p) => matchesTab(p, 'text')).length,
    reshares: reshares.filter((r) => r.expand?.post).length,
  }), [posts, reshares]);

  const visiblePosts = useMemo(() => posts.filter((p) => matchesTab(p, tab)), [posts, tab]);
  const visibleReshares = useMemo(() => reshares.filter((r) => r.expand?.post), [reshares]);

  /* ---------- render ---------- */
  const topbar = (title) => (
    <header className="pf-topbar">
      <div className="pf-topbar-in">
        <button type="button" className="pf-back" aria-label="Go back" onClick={() => (window.history.length > 1 ? nav(-1) : nav('/'))}>
          <ArrowBackRoundedIcon />
        </button>
        <h1>{title}</h1>
        <Navbar theme={theme} onToggleTheme={toggleTheme} />
      </div>
    </header>
  );

  if (notFound) {
    return (
      <div className="pf mf-feed" data-mf-theme={theme}>
        {topbar('Profile')}
        <div className="pf-wrap">
          <Empty icon="🕵️" title="Profile not found" text="This user doesn’t exist or the link is wrong." />
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="pf mf-feed" data-mf-theme={theme}>
        {topbar('Profile')}
        <div className="pf-wrap" aria-busy="true">
          <div className="pf-sk" style={{ height: 240, borderRadius: 22 }} />
          <div className="pf-sk" style={{ height: 190, marginTop: 14, borderRadius: 22 }} />
          <div className="pf-sk" style={{ height: 260, marginTop: 20, maxWidth: 720, marginInline: 'auto' }} />
        </div>
      </div>
    );
  }

  const name = userName(profile);
  const isPrivate = profile.showPublicProfile === false && !isMe;
  const joined = profile.created && !Number.isNaN(new Date(profile.created).getTime())
    ? new Date(profile.created).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
    : '';

  return (
    <div className="pf mf-feed" data-mf-theme={theme}>
      {topbar(name)}

      <div className="pf-wrap">
        {/* ---------- cover ---------- */}
        <div className="pf-cover">
          {coverUrls.length > 0 && (profile.coverType === 'video'
            ? <video autoPlay loop muted playsInline src={coverUrls[0]} />
            : <FileImg urls={coverUrls} />)}
          {!coverUrls.length && (
            <div style={{ position: 'absolute', inset: 0, background: 'var(--fr-grad)', opacity: 0.55 }} aria-hidden="true" />
          )}
          {isMe && (
            <div className="pf-cover-edit">
              <input ref={coverInput} type="file" accept="image/*,video/*" hidden onChange={(e) => { uploadFile('cover', e.target.files?.[0]); e.target.value = ''; }} />
              <button type="button" className="pf-glass" disabled={uploading === 'cover'} onClick={() => coverInput.current?.click()}>
                <PhotoCameraRoundedIcon /> {uploading === 'cover' ? 'Uploading…' : 'Change cover'}
              </button>
            </div>
          )}
        </div>

        {/* ---------- identity ---------- */}
        <section className="pf-card" aria-label="Profile details">
          <div className="pf-top">
            <div className="pf-ava">
              <FriendAvatar user={profile} size={120} radius="30px" />
              {isMe && (
                <>
                  <input ref={avatarInput} type="file" accept="image/*" hidden onChange={(e) => { uploadFile('avatar', e.target.files?.[0]); e.target.value = ''; }} />
                  <button type="button" className="pf-ava-edit" aria-label="Change profile photo" disabled={uploading === 'avatar'} onClick={() => avatarInput.current?.click()}>
                    <PhotoCameraRoundedIcon />
                  </button>
                </>
              )}
            </div>

            <div className="pf-actions">
              {isMe ? (
                <>
                  <button type="button" className="fr-btn fr-btn--primary" onClick={openEditor}><EditRoundedIcon /> Edit profile</button>
                  <button type="button" className="fr-btn" onClick={goToBrand}><StorefrontRoundedIcon /> Go to Brand</button>
                  <button type="button" className="fr-btn fr-btn--danger" onClick={doLogout}><LogoutRoundedIcon /> Logout</button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    className={`fr-btn ${isFollowing ? '' : 'fr-btn--primary'}`}
                    onClick={toggleFollow}
                    disabled={followBusy}
                    aria-pressed={isFollowing}
                  >
                    {isFollowing ? <><CheckRoundedIcon /> Following</> : <><PersonAddAlt1RoundedIcon /> Follow</>}
                  </button>
                  <button type="button" className="fr-btn" onClick={() => (user ? nav(`/chat/${profile.id}`) : nav('/login'))}>
                    <ChatBubbleRoundedIcon /> Message
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="pf-id">
            <h2 className="pf-name">{name}</h2>
            {profile.username && <span className="pf-handle">@{profile.username}</span>}

            <div className="pf-chips">
              <span><WorkOutlineRoundedIcon />{profile.profession || 'Creator'}</span>
              {profile.city && <span><LocationOnRoundedIcon />{profile.city}</span>}
              {joined && <span><CalendarMonthRoundedIcon />Joined {joined}</span>}
            </div>

            <p className={`pf-bio${profile.bio ? '' : ' pf-bio--empty'}`}>
              {profile.bio || 'This user has not written a bio yet.'}
            </p>

            {links.length > 0 && (
              <div className="pf-links">
                {links.map((l, i) => (
                  <a key={`${l.href}-${i}`} className="pf-link" href={l.href} target="_blank" rel="noopener noreferrer"><LinkRoundedIcon />{l.label}</a>
                ))}
              </div>
            )}

            {profile.peerId && (
              <button type="button" className="pf-copy" onClick={copyPeerId} title="Copy ID">
                <ContentCopyRoundedIcon /> My ID: {profile.peerId}
              </button>
            )}
          </div>

          <div className="pf-stats">
            <button type="button" className="pf-stat" onClick={openFollowers}>
              <b>{fmt(profile.followersCount ?? 0)}</b><span>Followers</span>
            </button>
            <div className="pf-stat"><b>{fmt(posts.length)}</b><span>Posts</span></div>
            <div className="pf-stat"><b>{fmt(counts.reshares)}</b><span>Reshares</span></div>
          </div>
        </section>

        {/* ---------- content ---------- */}
        {isPrivate ? (
          <div style={{ marginTop: 24 }}>
            <Empty icon={<LockRoundedIcon />} title="This profile is private" text={`${name} has chosen not to share their posts publicly.`} />
          </div>
        ) : (
          <>
            <div className="pf-tabs" role="tablist" aria-label="Profile content">
              {TAB_DEFS.map(({ id: tid, label, Icon }) => (
                <button
                  key={tid}
                  type="button"
                  role="tab"
                  aria-selected={tab === tid}
                  className={`pf-tab${tab === tid ? ' is-active' : ''}`}
                  onClick={() => setTab(tid)}
                >
                  <Icon /> {label}
                  <span className="pf-tab-n">{counts[tid]}</span>
                </button>
              ))}
            </div>

            <div className="pf-feed">
              {loadingPosts && (
                <>
                  <div className="pf-sk" style={{ height: 280 }} aria-hidden="true" />
                  <div className="pf-sk" style={{ height: 180 }} aria-hidden="true" />
                </>
              )}

              {!loadingPosts && tab !== 'reshares' && visiblePosts.length === 0 && (
                <Empty
                  icon="✨"
                  title={tab === 'all' ? 'No posts yet' : `No ${tab} posts`}
                  text={isMe ? 'Share something with the community — it will show up here.' : `${name} hasn’t posted anything here yet.`}
                />
              )}
              {!loadingPosts && tab !== 'reshares' && visiblePosts.map((p) => (
                <PostCard key={p.id} post={p} onDeleted={handlePostDeleted} />
              ))}

              {!loadingPosts && tab === 'reshares' && visibleReshares.length === 0 && (
                <Empty icon="🔁" title="No reshares yet" text={isMe ? 'Posts you reshare will appear here.' : `${name} hasn’t reshared anything yet.`} />
              )}
              {!loadingPosts && tab === 'reshares' && visibleReshares.map((r) => (
                <div key={r.id} className="pf-reshare">
                  <p className="pf-reshare-by"><RepeatRoundedIcon /> {isMe ? 'You reshared' : `${name} reshared`}</p>
                  <PostCard post={r.expand.post} />
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* ---------- edit dialog ---------- */}
      {editing && isMe && (
        <Modal
          title="Edit profile"
          onClose={() => setEditing(false)}
          footer={(
            <>
              <button type="button" className="fr-btn" onClick={() => setEditing(false)}>Cancel</button>
              <button type="button" className="fr-btn fr-btn--primary" onClick={saveProfile} disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</button>
            </>
          )}
        >
          <div className="pf-field">
            <label htmlFor="pf-name">Display name</label>
            <input id="pf-name" type="text" value={form.name} maxLength={60} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="pf-field">
            <label htmlFor="pf-prof">Profession</label>
            <input id="pf-prof" type="text" value={form.profession} maxLength={60} placeholder="Artist, developer, musician…" onChange={(e) => setForm({ ...form, profession: e.target.value })} />
          </div>
          <div className="pf-field">
            <label htmlFor="pf-city">City</label>
            <input id="pf-city" type="text" value={form.city} maxLength={60} onChange={(e) => setForm({ ...form, city: e.target.value })} />
          </div>
          <div className="pf-field">
            <label htmlFor="pf-bio">Bio</label>
            <textarea id="pf-bio" value={form.bio} maxLength={500} onChange={(e) => setForm({ ...form, bio: e.target.value })} />
          </div>

          <div className="pf-field">
            <span className="pf-label">Links (up to {MAX_LINKS})</span>
            {form.links.map((l, i) => (
              <div className="pf-linkrow" key={i}>
                <input
                  type="text"
                  value={l.label}
                  placeholder="Label"
                  aria-label={`Link ${i + 1} label`}
                  onChange={(e) => setForm({ ...form, links: form.links.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)) })}
                />
                <input
                  type="text"
                  value={l.url}
                  placeholder="example.com/me"
                  aria-label={`Link ${i + 1} address`}
                  onChange={(e) => setForm({ ...form, links: form.links.map((x, j) => (j === i ? { ...x, url: e.target.value } : x)) })}
                />
                <button type="button" className="fr-btn fr-btn--icon fr-btn--danger" aria-label={`Remove link ${i + 1}`} onClick={() => setForm({ ...form, links: form.links.filter((_, j) => j !== i) })}>
                  <DeleteOutlineRoundedIcon />
                </button>
              </div>
            ))}
            {form.links.length < MAX_LINKS && (
              <button type="button" className="fr-btn" style={{ alignSelf: 'flex-start' }} onClick={() => setForm({ ...form, links: [...form.links, { label: '', url: '' }] })}>
                <AddRoundedIcon /> Add link
              </button>
            )}
          </div>

          <label className="pf-switch">
            <span>
              <strong>Public profile</strong>
              <small>When off, others can’t see your posts.</small>
            </span>
            <input type="checkbox" checked={form.showPublicProfile} onChange={(e) => setForm({ ...form, showPublicProfile: e.target.checked })} />
          </label>
        </Modal>
      )}

      {/* ---------- followers dialog ---------- */}
      {showFollowers && (
        <Modal title={`Followers · ${fmt(profile.followersCount ?? followersList.length)}`} onClose={() => setShowFollowers(false)}>
          {loadingFollowers && (
            <>
              <div className="pf-sk" style={{ height: 58 }} aria-hidden="true" />
              <div className="pf-sk" style={{ height: 58 }} aria-hidden="true" />
            </>
          )}
          {!loadingFollowers && followersList.length === 0 && <Empty icon="👥" title="No followers yet" />}
          {!loadingFollowers && followersList.length > 0 && (
            <ul className="pf-people">
              {followersList.map((f) => {
                const person = f.expand?.follower;
                if (!person) return null;
                return (
                  <li key={f.id}>
                    <button
                      type="button"
                      className="pf-person"
                      onClick={() => { setShowFollowers(false); nav(`/profile/${person.id}`); }}
                    >
                      <FriendAvatar user={person} size={42} />
                      <span>
                        <b>{userName(person)}</b>
                        {person.username && <small>@{person.username}</small>}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </Modal>
      )}

      {toast && <div className="pf-toast" role="status">{toast}</div>}
    </div>
  );
}